import React, { useEffect, useState } from 'react'
import { X } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import {
  curatedSourceService,
  CuratedFeedTopic,
  CuratedSource
} from '@/services/curatedSourceService'
import { useToast } from '@/contexts/ToastContext'

const TOPIC_OPTIONS = Object.values(CuratedFeedTopic)

interface CuratedSourceFormModalProps {
  isOpen: boolean
  source: CuratedSource | null
  onClose: () => void
  onSaved: (source: CuratedSource) => void
}

const CuratedSourceFormModal: React.FC<CuratedSourceFormModalProps> = ({
  isOpen,
  source,
  onClose,
  onSaved
}) => {
  const { showToast } = useToast()
  const isEditMode = source !== null

  const [name, setName] = useState('')
  const [feedUrl, setFeedUrl] = useState('')
  const [topic, setTopic] = useState<CuratedFeedTopic>(CuratedFeedTopic.LEADERSHIP_MANAGEMENT)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})

  useEffect(() => {
    if (isOpen) {
      setName(source?.name ?? '')
      setFeedUrl(source?.feedUrl ?? '')
      setTopic(source?.topic ?? CuratedFeedTopic.LEADERSHIP_MANAGEMENT)
      setErrors({})
    }
  }, [isOpen, source])

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!name.trim()) {
      newErrors.name = 'Name is required'
    }
    if (!isEditMode && !feedUrl.trim()) {
      newErrors.feedUrl = 'Feed URL is required'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    setIsSubmitting(true)

    try {
      const saved = isEditMode
        ? await curatedSourceService.updateSource(source!.id, { name: name.trim(), topic })
        : await curatedSourceService.createSource({ name: name.trim(), feedUrl: feedUrl.trim(), topic })

      showToast({ type: 'success', message: isEditMode ? 'Source updated' : 'Source created' })
      onSaved(saved)
      onClose()
    } catch (error) {
      showToast({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to save curated source'
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={onClose} />

        <div className="relative bg-white rounded-lg shadow-xl max-w-lg w-full">
          <div className="border-b px-6 py-4 flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">
              {isEditMode ? 'Edit Source' : 'Add Source'}
            </h2>
            <button onClick={onClose} disabled={isSubmitting} className="text-gray-400 hover:text-gray-600 disabled:opacity-50">
              <X className="w-5 h-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-6">
            <div className="mb-4">
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                Name *
              </label>
              <input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Harvard Business Review"
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                  errors.name ? 'border-red-300' : 'border-gray-300'
                }`}
                disabled={isSubmitting}
                maxLength={200}
              />
              {errors.name && <p className="text-sm text-red-500 mt-1">{errors.name}</p>}
            </div>

            <div className="mb-4">
              <label htmlFor="feedUrl" className="block text-sm font-medium text-gray-700 mb-1">
                Feed URL *
              </label>
              <input
                id="feedUrl"
                type="url"
                value={feedUrl}
                onChange={(e) => setFeedUrl(e.target.value)}
                placeholder="https://example.com/feed"
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                  errors.feedUrl ? 'border-red-300' : 'border-gray-300'
                } ${isEditMode ? 'bg-gray-100 text-gray-500' : ''}`}
                disabled={isSubmitting || isEditMode}
              />
              {errors.feedUrl && <p className="text-sm text-red-500 mt-1">{errors.feedUrl}</p>}
            </div>

            <div className="mb-6">
              <label htmlFor="topic" className="block text-sm font-medium text-gray-700 mb-1">
                Topic *
              </label>
              <select
                id="topic"
                value={topic}
                onChange={(e) => setTopic(e.target.value as CuratedFeedTopic)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                disabled={isSubmitting}
              >
                {TOPIC_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex gap-3 justify-end border-t pt-4">
              <Button type="button" variant="secondary" onClick={onClose} disabled={isSubmitting}>
                Cancel
              </Button>
              <Button type="submit" variant="primary" disabled={isSubmitting} isLoading={isSubmitting}>
                {isEditMode ? 'Save Changes' : 'Add Source'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default CuratedSourceFormModal
